import { useEffect, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { aoSalvarNaAba, tirarDaAba } from '../lib/clientes';
import type { Reg } from '../store/types';
import type { Colecao, Ctx, ModDef, PainelProps } from './tipos';
import { contagem, dataBr, din, junta, num } from './util';
import { excluirReg, mudarCampos, novoReg, salvarReg } from '../store/modulos';
import { Chip } from '../components/ui';
import { C, alpha } from '../components/theme';
import { today } from '../lib/date';
import { N_, t } from '../lib/i18n';

const COLUNAS = [N_('A fazer'), N_('Em andamento'), N_('Aprovação'), N_('Concluído')];
const ICONE_COL = ['📝', '🚧', '👀', '✅'];
const horas = (min: number) => `${Math.floor(min / 60)}h${String(Math.round(min % 60)).padStart(2, '0')}`;
const doMes = (r: Reg) => String(r.c.data || '').slice(0, 7) === today().slice(0, 7);
const nomeCliente = (ctx: Ctx, id: string) => ctx.porId(id)?.c.nome ?? '';

/** Quadro Kanban: 4 colunas, os cartões andam com ◀ ▶. */
function Kanban({ ctx, lista, abrir, novo }: PainelProps) {
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={{ gap: 10, paddingBottom: 6 }}>
      {COLUNAS.map((col, i) => {
        const cartoes = lista.filter(r => (r.c.coluna || COLUNAS[0]) === col).sort((a, b) => String(a.c.prazo || '9').localeCompare(String(b.c.prazo || '9')));
        return (
          <View key={col} style={{ width: 250, backgroundColor: C.card, borderRadius: 14, padding: 10, gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>{ICONE_COL[i]} {t(col)} <Text style={{ color: C.mute }}>{cartoes.length}</Text></Text>
              <Pressable onPress={() => novo({ coluna: col })} hitSlop={8}><Text style={{ color: ctx.cor, fontSize: 20, fontWeight: '700' }}>+</Text></Pressable>
            </View>
            {cartoes.map(r => {
              const cont = i < 3 ? contagem(r.c.prazo) : null;
              const it = r.itens ?? [];
              return (
                <Pressable key={r.id} onPress={() => abrir(r)} style={{ backgroundColor: C.card2, borderRadius: 12, padding: 10, gap: 4, borderLeftWidth: 3, borderLeftColor: ctx.cor }}>
                  <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={3}>{r.c.titulo || t('Sem título')}</Text>
                  {!!r.c.cliente && <Text style={{ color: C.sub, fontSize: 12 }}>👤 {nomeCliente(ctx, r.c.cliente)}</Text>}
                  {!!cont && <Text style={{ color: cont.cor, fontSize: 12, fontWeight: '700' }}>📅 {dataBr(r.c.prazo)} · {cont.txt}</Text>}
                  {!!it.length && <Text style={{ color: C.sub, fontSize: 12 }}>☑ {it.filter(x => x.done).length}/{it.length}</Text>}
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                    <Pressable disabled={i === 0} onPress={() => mudarCampos(ctx.mod, r.id, { coluna: COLUNAS[i - 1] })} hitSlop={8} style={{ opacity: i === 0 ? 0.25 : 1, paddingHorizontal: 8 }}><Text style={{ color: C.text, fontSize: 16 }}>◀</Text></Pressable>
                    <Pressable disabled={i === 3} onPress={() => mudarCampos(ctx.mod, r.id, { coluna: COLUNAS[i + 1], ...(i + 1 === 3 ? { concluidoEm: today() } : {}) })} hitSlop={8} style={{ opacity: i === 3 ? 0.25 : 1, paddingHorizontal: 8 }}><Text style={{ color: C.text, fontSize: 16 }}>▶</Text></Pressable>
                  </View>
                </Pressable>
              );
            })}
            {!cartoes.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Nenhum cartão')}</Text>}
          </View>
        );
      })}
    </ScrollView>
  );
}

/** Cronômetro de horas por cliente. Fica guardado na conta: dá para começar no celular e parar no computador. */
function Cronometro({ ctx }: PainelProps) {
  const rel = ctx.regs.find(r => r.col === 'relogio');
  const clientes = ctx.regs.filter(r => r.col === 'cliente');
  const [cliente, setCliente] = useState<string>('');
  const [, tick] = useState(0);
  useEffect(() => { if (!rel) return; const i = setInterval(() => tick(x => x + 1), 1000); return () => clearInterval(i); }, [rel]);
  const decorrido = rel ? Math.max(0, Date.now() - new Date(rel.c.inicio).getTime()) : 0;
  const hms = (ms: number) => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`; };
  function iniciar() { salvarReg(ctx.mod, novoReg('relogio', null, { inicio: new Date().toISOString(), cliente })); }
  function parar() {
    if (!rel) return;
    const min = Math.max(1, Math.round(decorrido / 60000));
    salvarReg(ctx.mod, novoReg('sessao', null, { cliente: rel.c.cliente || '', data: today(), minutos: min, desc: '' }));
    excluirReg(ctx.mod, rel.id);
  }
  return (
    <View style={{ backgroundColor: alpha(ctx.cor, 0.14), borderRadius: 14, padding: 12, gap: 8, borderWidth: 1, borderColor: alpha(ctx.cor, 0.4) }}>
      <Text style={{ color: C.text, fontWeight: '800' }}>⏱️ {rel ? t('Contando: {cliente}', { cliente: nomeCliente(ctx, rel.c.cliente) || t('sem cliente') }) : t('Cronômetro de horas')}</Text>
      {rel ? <Text style={{ color: C.text, fontSize: 32, fontWeight: '800', fontVariant: ['tabular-nums'] }}>{hms(decorrido)}</Text> : (
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {clientes.map(c => <Chip key={c.id} small cor={ctx.cor} on={cliente === c.id} label={c.c.nome || '?'} onPress={() => setCliente(cliente === c.id ? '' : c.id)} />)}
          {!clientes.length && <Text style={{ color: C.mute, fontSize: 12 }}>{t('Cadastre os clientes para separar as horas.')}</Text>}
        </View>
      )}
      <Pressable onPress={rel ? parar : iniciar} style={{ backgroundColor: rel ? C.danger : ctx.cor, borderRadius: 12, padding: 12, alignItems: 'center' }}>
        <Text style={{ color: '#fff', fontWeight: '800' }}>{rel ? '⏹ ' + t('Parar e salvar') : '▶ ' + t('Começar')}</Text>
      </Pressable>
    </View>
  );
}

const cartao: Colecao = {
  id: 'cartao', nome: N_('Quadro'), um: N_('Cartão'), icone: '🗂️',
  campos: [
    { k: 'titulo', r: N_('Tarefa ou entrega'), t: 'texto' },
    { k: 'cliente', r: N_('Cliente'), t: 'ref', ref: 'cliente' },
    { k: 'coluna', r: N_('Coluna'), t: 'opcao', op: COLUNAS },
    { k: 'prazo', r: N_('Prazo'), t: 'data', aviso: [2, 0] },
    { k: 'valor', r: N_('Valor'), t: 'dinheiro' },
    { k: 'notas', r: N_('Detalhes'), t: 'longo' },
  ],
  titulo: r => r.c.titulo || '', data: 'prazo', itens: N_('Checklist'), anexos: true,
  feito: r => r.c.coluna === 'Concluído',
  novo: () => ({ coluna: COLUNAS[0] }),
  agenda: (r, ctx) => (r.c.prazo ? { start: r.c.prazo, title: t('Prazo: {t}', { t: r.c.titulo }), notes: nomeCliente(ctx, r.c.cliente) } : null),
  lista: Kanban,
};

const sessao: Colecao = {
  id: 'sessao', nome: N_('Horas'), um: N_('Registro de horas'), icone: '⏱️', semDuplicar: () => true,   // texto 26 (B7)
  campos: [
    { k: 'cliente', r: N_('Cliente'), t: 'ref', ref: 'cliente' },
    { k: 'data', r: N_('Data'), t: 'data' },
    { k: 'minutos', r: N_('Minutos'), t: 'numero', un: 'min' },
    { k: 'desc', r: N_('O que foi feito'), t: 'texto' },
  ],
  titulo: (r, ctx) => junta(horas(num(r.c.minutos)), nomeCliente(ctx, r.c.cliente)), sub: r => junta(dataBr(r.c.data), r.c.desc),
  ordem: 'data-desc', novo: () => ({ data: today() }),
  painel: Cronometro,
  resumo: (l, ctx) => {
    const mes = l.filter(doMes);
    if (!mes.length) return null;
    const por = new Map<string, number>();
    for (const r of mes) por.set(r.c.cliente || '', (por.get(r.c.cliente || '') ?? 0) + num(r.c.minutos));
    const valor = [...por].reduce((s, [c, m]) => s + (num(ctx.porId(c)?.c.valorHora) * m) / 60, 0);
    return t('Este mês: {h}', { h: horas([...por.values()].reduce((a, b) => a + b, 0)) }) + (valor ? ' · ' + t('a receber {v}', { v: din(valor, ctx.moeda) }) : '')
      + '\n' + [...por].map(([c, m]) => `${nomeCliente(ctx, c) || t('sem cliente')}: ${horas(m)}`).join(' · ');
  },
};

const cliente: Colecao = {
  id: 'cliente', nome: N_('Clientes'), um: N_('Cliente'), icone: '👤',
  campos: [
    { k: 'nome', r: N_('Nome'), t: 'texto' },
    { k: 'contato', r: N_('Pessoa de contato'), t: 'texto' },
    { k: 'telefone', r: N_('Telefone'), t: 'telefone' },
    { k: 'email', r: N_('E-mail'), t: 'email' },
    { k: 'endereco', r: N_('Endereço'), t: 'endereco' },
    { k: 'valorHora', r: N_('Valor da hora'), t: 'dinheiro' },
    { k: 'notas', r: N_('Observações'), t: 'longo' },
  ],
  titulo: r => r.c.nome || '',
  sub: (r, ctx) => { const min = ctx.regs.filter(x => x.col === 'sessao' && x.c.cliente === r.id && doMes(x)).reduce((s, x) => s + num(x.c.minutos), 0);
    return junta(r.c.valorHora !== '' && r.c.valorHora != null && t('{v}/hora', { v: din(r.c.valorHora, ctx.moeda) }), min && t('{h} este mês', { h: horas(min) }),
      t('{n} cartão(ões) abertos', { n: ctx.regs.filter(x => x.col === 'cartao' && x.c.cliente === r.id && x.c.coluna !== 'Concluído').length })); },
  ordem: 'titulo',
  aoSalvar: (r, ctx) => aoSalvarNaAba(ctx.mod, r), aoExcluir: (r, ctx) => tirarDaAba(ctx.mod, r),   // texto 25: ligada a Meus clientes
};

export const FREELANCER: ModDef = { id: 'freelancer', colecoes: [cartao, sessao, cliente] };
